
import * as THREE from "../../../libs/three.js/build/three.module.js";

// Robust 3D cylinder fitting for the Pipe tool. Pure geometry/math, no
// scene/DOM/point-cloud-query dependencies - see PipeCandidates.js for
// gathering the actual candidate points this operates on.
//
// Overall approach (see pipeTool.js for how the stages below are driven):
//   1. initial axis from the two user-picked direction points (rough guess
//      only - see refineCylinder(), which is explicitly allowed to correct
//      both axis direction and center away from this initial guess);
//   2. initial circle from the 5 seed points, projected onto the plane
//      perpendicular to the initial axis (fitCircle2D on 5 points);
//   3. RANSAC-lite over the gathered candidate points: repeatedly refit from
//      random small subsets, keep whichever fit has the most inliers
//      (fitCylinderRANSAC);
//   4. IRLS refinement on the winning inlier set - a few more alternating
//      iterations, but weighting each point by a robust (Cauchy-like)
//      function of its residual instead of a hard in/out cutoff, so a
//      handful of remaining ground/noise points can't drag the result
//      (refineCylinder with per-point weights).
//
// The core alternating step in both RANSAC-refit and IRLS-refit
// (refineCylinder) is: given a current axis, fit a (weighted) 2D circle to
// the points projected onto the perpendicular plane (gives an updated
// center + radius); then slice the same points into bins along the axis,
// fit a circle *per slice*, and fit a 3D line through those per-slice
// centers (gives an updated axis direction+point) - this is what lets the
// axis direction itself be corrected from the real point-cloud geometry
// rather than staying pinned to the user's rough initial pick.

const EPS = 1e-9;

// Maximum angular correction the fit is allowed to apply to the user's own
// two-direction-point axis guess, across the whole RANSAC+IRLS pipeline
// (see clampAxisDeviation()). A user tracing along a visible pipe should
// land within a few degrees of the true axis; this budget is generous
// enough to absorb normal click imprecision and real (if modest) curvature
// in the visible section, while still rejecting the tens-of-degrees swings
// that come from candidate contamination rather than genuine correction.
export const DEFAULT_MAX_AXIS_DEVIATION_RAD = 20 * Math.PI / 180;

function clamp01(x){
	return Math.max(0, Math.min(1, x));
}

// Caps how far `candidateDir` may deviate from `referenceDir`, rotating it
// back towards the reference (around the shortest-path axis between the
// two) if it exceeds `maxAngleRad`. Used to anchor the axis-refinement steps
// below to the user's own two direction-points: the fit is explicitly meant
// to be able to *correct* that initial guess (spec), but an unconstrained
// per-iteration 3D line fit through a handful of noisy per-slice circle
// centers (see refineCylinder()) can occasionally swing the axis by tens of
// degrees when the gathered candidates are contaminated by a nearby
// non-pipe surface - seen in practice as a visibly "rotated" cylinder result
// relative to the direction the user actually pointed out. Capping the
// deviation keeps genuine small corrections while rejecting that failure
// mode outright, rather than trying to out-guess which contaminated points
// caused it.
function clampAxisDeviation(candidateDir, referenceDir, maxAngleRad){
	const ref = referenceDir.clone().normalize();
	const cand = candidateDir.clone().normalize();
	const dot = Math.max(-1, Math.min(1, ref.dot(cand)));
	const angle = Math.acos(dot);
	if(angle <= maxAngleRad || angle < EPS) return cand;

	const rotAxis = new THREE.Vector3().crossVectors(ref, cand);
	if(rotAxis.lengthSq() < EPS) return cand; // (near-)antiparallel: no well-defined rotation plane, leave as-is

	rotAxis.normalize();
	const q = new THREE.Quaternion().setFromAxisAngle(rotAxis, maxAngleRad);
	return ref.clone().applyQuaternion(q).normalize();
}

// two unit vectors spanning the plane perpendicular to `axisDir`, chosen
// consistently (no arbitrary flips) so repeated calls with a slowly-changing
// axis produce a slowly-changing (not randomly rotated) basis
export function orthonormalBasis(axisDir){
	const helper = Math.abs(axisDir.z) < 0.9 ? new THREE.Vector3(0, 0, 1) : new THREE.Vector3(1, 0, 0);
	const u = new THREE.Vector3().crossVectors(helper, axisDir).normalize();
	const v = new THREE.Vector3().crossVectors(axisDir, u).normalize();
	return {u, v};
}

// weighted algebraic (Kasa) circle fit through 2D points - closed-form
// linear least squares, no iteration needed. Returns null if the points are
// (near-)collinear or too few to determine a circle.
export function fitCircle2D(points2D, weights = null){
	const n = points2D.length;
	if(n < 3) return null;

	// normal equations for A*[cx,cy,c]^T = b, where for each point (x,y):
	// 2*x*cx + 2*y*cy + c = x^2+y^2, c = r^2 - cx^2 - cy^2
	let Sxx = 0, Sxy = 0, Sx = 0, Syy = 0, Sy = 0, S1 = 0;
	let Sxz = 0, Syz = 0, Sz = 0;

	for(let i = 0; i < n; i++){
		const w = weights ? weights[i] : 1;
		const x = points2D[i].x, y = points2D[i].y;
		const z = x * x + y * y;

		Sxx += w * x * x; Sxy += w * x * y; Sx += w * x;
		Syy += w * y * y; Sy += w * y; S1 += w;
		Sxz += w * x * z; Syz += w * y * z; Sz += w * z;
	}

	// solve the 3x3 system [[2Sxx,2Sxy,Sx],[2Sxy,2Syy,Sy],[2Sx,2Sy,S1]] * [cx,cy,c] = [Sxz,Syz,Sz]
	const A = [
		[2 * Sxx, 2 * Sxy, Sx],
		[2 * Sxy, 2 * Syy, Sy],
		[2 * Sx, 2 * Sy, S1],
	];
	const b = [Sxz, Syz, Sz];

	const solved = solve3x3(A, b);
	if(!solved) return null;

	const [cx, cy, c] = solved;
	const rSq = c + cx * cx + cy * cy;
	if(!(rSq > 0)) return null;

	return {cx, cy, r: Math.sqrt(rSq)};
}

function solve3x3(A, b){
	const det = (m) =>
		m[0][0] * (m[1][1] * m[2][2] - m[1][2] * m[2][1]) -
		m[0][1] * (m[1][0] * m[2][2] - m[1][2] * m[2][0]) +
		m[0][2] * (m[1][0] * m[2][1] - m[1][1] * m[2][0]);

	const D = det(A);
	if(Math.abs(D) < EPS) return null;

	const replaceCol = (col) => A.map((row, i) => row.map((v, j) => j === col ? b[i] : v));

	const Dx = det(replaceCol(0));
	const Dy = det(replaceCol(1));
	const Dz = det(replaceCol(2));

	return [Dx / D, Dy / D, Dz / D];
}

// dominant eigenvector of a 3x3 symmetric matrix via power iteration - used
// to fit a 3D line's direction through a small set of points (their
// covariance matrix's largest-eigenvalue eigenvector is the best-fit
// direction, i.e. total least squares)
function dominantEigenvector3x3(M, iterations = 50){
	let v = new THREE.Vector3(1, 1, 1).normalize();
	for(let i = 0; i < iterations; i++){
		const nv = new THREE.Vector3(
			M[0][0] * v.x + M[0][1] * v.y + M[0][2] * v.z,
			M[1][0] * v.x + M[1][1] * v.y + M[1][2] * v.z,
			M[2][0] * v.x + M[2][1] * v.y + M[2][2] * v.z,
		);
		if(nv.lengthSq() < EPS) break;
		nv.normalize();
		v = nv;
	}
	return v;
}

// total-least-squares best-fit 3D line through a set of points
function fitLine3D(points){
	if(points.length < 2) return null;

	const centroid = new THREE.Vector3();
	for(const p of points) centroid.add(p);
	centroid.divideScalar(points.length);

	const M = [[0, 0, 0], [0, 0, 0], [0, 0, 0]];
	for(const p of points){
		const d = p.clone().sub(centroid);
		M[0][0] += d.x * d.x; M[0][1] += d.x * d.y; M[0][2] += d.x * d.z;
		M[1][0] += d.y * d.x; M[1][1] += d.y * d.y; M[1][2] += d.y * d.z;
		M[2][0] += d.z * d.x; M[2][1] += d.z * d.y; M[2][2] += d.z * d.z;
	}

	const direction = dominantEigenvector3x3(M);
	return {point: centroid, direction};
}

// perpendicular distance from `point` to the infinite line (axisPoint, axisDir)
// (varm funktion: ingen Vector3-allokering pr. kald - samme aritmetik og rækkefølge som Vector3-udgaven, dvs. bit-identisk resultat)
export function distanceToAxis(point, axisPoint, axisDir){
	const dx = point.x - axisPoint.x, dy = point.y - axisPoint.y, dz = point.z - axisPoint.z;
	const along = dx * axisDir.x + dy * axisDir.y + dz * axisDir.z;
	const cx = dx - axisDir.x * along, cy = dy - axisDir.y * along, cz = dz - axisDir.z * along;
	return Math.sqrt(cx * cx + cy * cy + cz * cz);
}

// signed parameter (world units) of `point`'s projection along the axis,
// relative to axisPoint
export function paramAlongAxis(point, axisPoint, axisDir){
	return (point.x - axisPoint.x) * axisDir.x + (point.y - axisPoint.y) * axisDir.y + (point.z - axisPoint.z) * axisDir.z;
}

// One alternating refinement step: given points (+ optional per-point
// weights) and a current axis guess, produce an updated {axisPoint,
// axisDir, radius}. See the module doc comment above for the two-part
// alternation this performs (circle fit for center/radius, then
// slice-and-line-fit for axis direction).
export function refineCylinder(points, weights, axisPointGuess, axisDirGuess, sliceCount = 5, options = {}){
	const axisDir = axisDirGuess.clone().normalize();
	const {u, v} = orthonormalBasis(axisDir);

	const proj = points.map((p) => {
		const d = p.clone().sub(axisPointGuess);
		return {x: d.dot(u), y: d.dot(v), t: d.dot(axisDir)};
	});

	const circle = fitCircle2D(proj, weights);
	if(!circle) return null;

	const center = axisPointGuess.clone().addScaledVector(u, circle.cx).addScaledVector(v, circle.cy);

	// slice along the (still-old) axis direction into bins, fit a 2D circle
	// per bin, then fit a 3D line through the per-bin centers - this is what
	// lets the axis direction itself move, not just the center/radius
	let tMin = Infinity, tMax = -Infinity;
	for(const pr of proj){ tMin = Math.min(tMin, pr.t); tMax = Math.max(tMax, pr.t); }

	const sliceCenters = [];
	if(tMax > tMin + EPS){
		const span = tMax - tMin;
		for(let s = 0; s < sliceCount; s++){
			const lo = tMin + span * (s / sliceCount);
			const hi = tMin + span * ((s + 1) / sliceCount);
			const sliceIdx = [];
			for(let i = 0; i < proj.length; i++){
				if(proj[i].t >= lo && proj[i].t <= hi) sliceIdx.push(i);
			}
			if(sliceIdx.length < 3) continue;

			const slicePoints = sliceIdx.map((i) => ({x: proj[i].x, y: proj[i].y}));
			const sliceWeights = weights ? sliceIdx.map((i) => weights[i]) : null;
			const sliceCircle = fitCircle2D(slicePoints, sliceWeights);
			if(!sliceCircle) continue;

			// mean t (position along the *old* axis) of this slice's own
			// points - without adding this back in, every slice center would
			// be reconstructed on the same plane through axisPointGuess,
			// throwing away the one thing (axial separation between slices)
			// that lets fitLine3D below detect a tilted axis at all. This was
			// missing before and made the whole slice-based axis correction
			// fit a "line" through points with ~zero true axial spread -
			// effectively fitting noise, which is what caused the axis to
			// occasionally jump by up to ~90 degrees in a single refinement
			// pass even when started from the correct axis (confirmed against
			// real point-cloud data, see pipeline.las testing notes).
			let sliceT = 0;
			for(const i of sliceIdx) sliceT += proj[i].t;
			sliceT /= sliceIdx.length;

			sliceCenters.push(axisPointGuess.clone().addScaledVector(u, sliceCircle.cx).addScaledVector(v, sliceCircle.cy).addScaledVector(axisDir, sliceT));
		}
	}

	let newAxisDir = axisDir;
	let newAxisPoint = center;
	if(sliceCenters.length >= 2){
		const line = fitLine3D(sliceCenters);
		if(line){
			// keep the axis pointing the "same way" as before (fitLine3D's
			// eigenvector sign is arbitrary) so it doesn't flip 180 deg
			// between iterations
			let candidate = line.direction.dot(axisDir) < 0 ? line.direction.clone().negate() : line.direction;
			if(options.axisDirReference && options.maxAxisDeviationRad != null){
				candidate = clampAxisDeviation(candidate, options.axisDirReference, options.maxAxisDeviationRad);
			}
			newAxisDir = candidate;
			newAxisPoint = line.point;
		}
	}

	return {axisPoint: newAxisPoint, axisDir: newAxisDir, radius: circle.r};
}

// RANSAC-lite: repeatedly refit from small random subsets of `candidates`,
// scoring each fit by its inlier count (points within `inlierThreshold` of
// the fitted surface). `inlierThreshold` is in world units (same as the
// point cloud coordinates).
//
// Returns the top `options.keepTopK` (default 1) hypotheses by inlier count,
// sorted best-first, as an array (empty if none could be fit) - review
// §19/§22/§28: raw inlier count alone is not a safe way to pick a single
// winner, since a competing surface's random sample can occasionally
// out-score the real pipe's on inlier count in one iteration, discarding a
// better (e.g. seed-consistent) candidate found in a different iteration.
// Callers that need the old single-best behavior can pass keepTopK: 1 (the
// default) and read result[0]; pipeAnalysis.js requests several and runs
// every one of them through its own hard gates, picking the best of
// whichever ones actually pass rather than trusting inlier count alone.
export function fitCylinderRANSAC(candidates, initialAxisPoint, initialAxisDir, initialRadius, options = {}){
	const iterations = options.iterations || 60;
	const sampleSize = Math.min(options.sampleSize || 14, candidates.length);
	const inlierThreshold = options.inlierThreshold || Math.max(initialRadius * 0.06, 0.01);
	// anchor every random-subset refit to the user's own two direction
	// points (see clampAxisDeviation()'s doc comment) - defaults to the
	// initial axis guess itself, since that *is* the user-supplied direction
	// at this stage of the pipeline
	const axisDirReference = options.axisDirReference || initialAxisDir;
	const maxAxisDeviationRad = options.maxAxisDeviationRad != null ? options.maxAxisDeviationRad : DEFAULT_MAX_AXIS_DEVIATION_RAD;
	const keepTopK = options.keepTopK || 1;

	if(candidates.length < sampleSize) return [];

	const results = [];

	for(let iter = 0; iter < iterations; iter++){
		const sample = randomSample(candidates, sampleSize);
		const fit = refineCylinder(sample, null, initialAxisPoint, initialAxisDir, 5, {axisDirReference, maxAxisDeviationRad});
		if(!fit || !isFinite(fit.radius) || fit.radius <= 0) continue;

		let inlierCount = 0;
		for(const p of candidates){
			const d = Math.abs(distanceToAxis(p, fit.axisPoint, fit.axisDir) - fit.radius);
			if(d <= inlierThreshold) inlierCount++;
		}

		results.push({fit, inlierCount});
	}

	results.sort((a, b) => b.inlierCount - a.inlierCount);
	return results.slice(0, keepTopK);
}

// Groups points onto a uniform 3D voxel grid and keeps one representative
// (the centroid) per occupied voxel - density normalization (review §16)
// applied to the *fitting* population before RANSAC/IRLS, so a denser
// nearby surface (e.g. a trench wall scanned at higher point density than
// the pipe itself) can't dominate inlier counts and robust-fit weighting
// purely by raw point volume. The caller keeps using the full-resolution
// point set for final QA (residuals, coverage, seed checks), so
// support/confidence numbers still reflect the real data.
export function voxelDownsample(points, voxelSize){
	const voxels = new Map();
	for(const p of points){
		const key = `${Math.floor(p.x / voxelSize)}_${Math.floor(p.y / voxelSize)}_${Math.floor(p.z / voxelSize)}`;
		let entry = voxels.get(key);
		if(!entry){
			entry = {sum: new THREE.Vector3(), count: 0};
			voxels.set(key, entry);
		}
		entry.sum.add(p);
		entry.count++;
	}

	const result = [];
	for(const {sum, count} of voxels.values()){
		result.push(sum.divideScalar(count));
	}
	return result;
}

function randomSample(array, count){
	if(array.length <= count) return array.slice();
	const indices = new Set();
	while(indices.size < count){
		indices.add(Math.floor(Math.random() * array.length));
	}
	return Array.from(indices).map((i) => array[i]);
}

// Cauchy-like robust weight from a residual (0 residual -> weight 1,
// large residual -> weight -> 0) - standard IRLS weighting function
export function robustWeight(residual, scale){
	const r = residual / scale;
	return 1 / (1 + r * r);
}

// IRLS refinement on a candidate inlier set: a few more alternating
// refineCylinder() passes, but weighting every point by a robust function of
// its current residual instead of a hard inlier/outlier split - lets a few
// remaining non-pipe points contribute a little without dominating.
export function refineCylinderIRLS(points, axisPoint, axisDir, radius, options = {}){
	const iterations = options.iterations || 5;
	const scale = options.scale || Math.max(radius * 0.08, 0.015);
	// anchored to the user's original two direction points (options.axisDirReference,
	// passed by pipeAnalysis.js as axisDir0), NOT the RANSAC-stage axis this
	// function starts from - so the cap bounds total drift across the whole
	// pipeline, not just this stage's own iterations
	const axisDirReference = options.axisDirReference || axisDir;
	const maxAxisDeviationRad = options.maxAxisDeviationRad != null ? options.maxAxisDeviationRad : DEFAULT_MAX_AXIS_DEVIATION_RAD;

	let current = {axisPoint, axisDir, radius};
	const radiusHistory = [radius];

	for(let it = 0; it < iterations; it++){
		const weights = points.map((p) => {
			const residual = Math.abs(distanceToAxis(p, current.axisPoint, current.axisDir) - current.radius);
			return robustWeight(residual, scale);
		});

		const refined = refineCylinder(points, weights, current.axisPoint, current.axisDir, 5, {axisDirReference, maxAxisDeviationRad});
		if(!refined || !isFinite(refined.radius) || refined.radius <= 0) break;

		current = refined;
		radiusHistory.push(current.radius);
	}

	return {...current, radiusHistory};
}

// smallest-eigenvalue eigenvector of a 3x3 symmetric matrix, reusing the
// existing dominant-eigenvector power iteration via a trace shift: for a
// symmetric M with eigenvalues lambda_i, (trace(M)*I - M) has eigenvalues
// (trace(M) - lambda_i) in the same eigenbasis, so its DOMINANT eigenvector
// (largest trace(M)-lambda_i, i.e. smallest original lambda_i) is exactly the
// eigenvector we want - no separate eigensolver needed.
function smallestEigenvector3x3(M){
	const trace = M[0][0] + M[1][1] + M[2][2];
	const shifted = [
		[trace - M[0][0], -M[0][1], -M[0][2]],
		[-M[1][0], trace - M[1][1], -M[1][2]],
		[-M[2][0], -M[2][1], trace - M[2][2]],
	];
	return dominantEigenvector3x3(shifted);
}

// sum of outer products d*d^T over the given (already-centered, or not -
// caller's choice) vectors - the shared building block behind both local
// surface-normal estimation and normal-based axis estimation below.
function scatterMatrix3x3(vectors){
	const M = [[0, 0, 0], [0, 0, 0], [0, 0, 0]];
	for(const d of vectors){
		M[0][0] += d.x * d.x; M[0][1] += d.x * d.y; M[0][2] += d.x * d.z;
		M[1][0] += d.y * d.x; M[1][1] += d.y * d.y; M[1][2] += d.y * d.z;
		M[2][0] += d.z * d.x; M[2][1] += d.z * d.y; M[2][2] += d.z * d.z;
	}
	return M;
}

function average(points){
	const sum = new THREE.Vector3();
	for(const p of points) sum.add(p);
	return sum.divideScalar(points.length);
}

// Per-point local surface normal via PCA on each point's own k nearest
// neighbors (brute-force - candidate sets here are small enough, see
// estimateAxisFromNormals' maxSample cap) - the normal is the smallest-
// eigenvalue eigenvector of the local neighborhood's scatter matrix (the
// direction the local patch varies least in), a standard point-cloud normal
// estimation technique. Sign is left ambiguous (n and -n are equivalent for
// a PCA-fit plane) - unneeded here since estimateAxisFromNormals only uses
// normals via outer products, which are sign-invariant.
export function estimateLocalNormals(points, k = 10){
	const n = points.length;
	if(n <= k) return null;

	const normals = [];
	for(let i = 0; i < n; i++){
		const p = points[i];
		const dists = [];
		for(let j = 0; j < n; j++){
			if(j === i) continue;
			dists.push({j, d: p.distanceToSquared(points[j])});
		}
		dists.sort((a, b) => a.d - b.d);

		const neighbors = [p];
		for(let m = 0; m < k; m++) neighbors.push(points[dists[m].j]);

		const centroid = average(neighbors);
		const deviations = neighbors.map((q) => q.clone().sub(centroid));
		normals.push(smallestEigenvector3x3(scatterMatrix3x3(deviations)));
	}
	return normals;
}

// Robust axis estimate from the identified pipe surface's local normals
// (review: next planned Pipe-tool improvement). For points genuinely on a
// cylinder, every local surface normal lies in the plane *perpendicular* to
// the axis - normals carry no component along it. So the axis is the
// smallest-eigenvalue eigenvector of the normals' own scatter matrix
// (sum of n*n^T over unit normals): that's the one direction the normal set
// has essentially no spread in, while the other two (the plane perpendicular
// to the axis) get full spread from normals pointing all the way around the
// pipe.
//
// This pools every point's local geometry into one global estimate rather
// than binning into slices first (see refineCylinder()'s slice-and-line-fit
// axis correction above) - intended to be more stable when there are too few
// points per slice for a good per-slice circle fit (narrow visible arc,
// short visible length, or noisy data).
//
// Deliberately conservative / fail-safe, since this is meant to sit
// alongside the existing slice-based estimate, not replace it or its
// downstream hard gates:
//  - returns null (caller should keep whatever axis it already had) if there
//    aren't comfortably enough points for reliable local normals, or if the
//    eigenvalue gap doesn't look decisive (ratio of the axis eigenvalue to
//    the total normal "energy" - trace(M), which equals normals.length since
//    each is unit length - isn't small enough to trust: a real cylinder's
//    normals should carry very little energy along the true axis, but noisy
//    or non-cylindrical data can leave the smallest eigenvalue ambiguous
//    rather than clearly separated from the other two).
//  - the returned axis is clamped to within `maxAxisDeviationRad` of
//    `axisDirReference` via the same clampAxisDeviation() used everywhere
//    else in this pipeline, so this can never move the axis further from the
//    user's own two direction points than the existing budget already allows.
export function estimateAxisFromNormals(points, options = {}){
	const k = options.k || 10;
	const maxSample = options.maxSample || 400;
	const minEigenvalueRatio = options.maxAxisEnergyFraction || 0.15;

	if(points.length <= k + 5) return null;

	const sample = points.length > maxSample ? randomSample(points, maxSample) : points;
	const normals = estimateLocalNormals(sample, k);
	if(!normals) return null;

	const M = scatterMatrix3x3(normals);
	const trace = M[0][0] + M[1][1] + M[2][2]; // == normals.length, since each is unit length
	if(trace < EPS) return null;

	let axis = smallestEigenvector3x3(M);

	const lambdaAxis = axis.x * (M[0][0] * axis.x + M[0][1] * axis.y + M[0][2] * axis.z)
		+ axis.y * (M[1][0] * axis.x + M[1][1] * axis.y + M[1][2] * axis.z)
		+ axis.z * (M[2][0] * axis.x + M[2][1] * axis.y + M[2][2] * axis.z);
	if(lambdaAxis / trace > minEigenvalueRatio) return null;

	if(options.axisDirReference && options.maxAxisDeviationRad != null){
		axis = clampAxisDeviation(axis, options.axisDirReference, options.maxAxisDeviationRad);
	}
	return axis;
}

// fraction (0..1) of the cylinder's circumference that the given points
// actually observe, computed by bucketing each point's angle (around the
// final axis, in the plane through `axisPoint`) into bins and counting how
// many bins contain at least one point - see pipeTool.js for how this feeds
// the quality classification (a handful of points all on the same small arc
// score low even if there are many of them - see spec's "flat top" example)
export function computeAngularCoverage(points, axisPoint, axisDir, binCount = 36){
	const {u, v} = orthonormalBasis(axisDir);
	const covered = new Array(binCount).fill(false);

	for(const p of points){
		const d = p.clone().sub(axisPoint);
		const x = d.dot(u), y = d.dot(v);
		let angle = Math.atan2(y, x);
		if(angle < 0) angle += Math.PI * 2;
		const bin = Math.min(binCount - 1, Math.floor((angle / (Math.PI * 2)) * binCount));
		covered[bin] = true;
	}

	const coveredBins = covered.filter(Boolean).length;
	return {
		fraction: coveredBins / binCount,
		degrees: (coveredBins / binCount) * 360,
	};
}

// Slices `points` into `binCount` bins along the axis and reports, per bin:
// how many points fall in it, its own locally-fitted radius (null if too few
// points to fit), and its own angular coverage - the shared building block
// for three independent post-fit checks (see pipeAnalysis.js): longitudinal
// support (is the fit backed by data spread across a real length, not one
// clustered sub-region?), radius stability along the pipe (does a real
// cylinder's radius stay consistent from bin to bin, unlike e.g. an uneven
// trench wall?), and coverage consistency (is the same arc genuinely visible
// throughout, rather than the overall coverage number being an artifact of
// different arcs stitched together from different positions along the axis?).
export function computeLongitudinalBins(points, axisPoint, axisDir, binCount = 6){
	const {u, v} = orthonormalBasis(axisDir);
	const proj = points.map((p) => {
		const d = p.clone().sub(axisPoint);
		return {x: d.dot(u), y: d.dot(v), t: d.dot(axisDir)};
	});

	let tMin = Infinity, tMax = -Infinity;
	for(const pr of proj){ tMin = Math.min(tMin, pr.t); tMax = Math.max(tMax, pr.t); }
	if(!(tMax > tMin + EPS)) return {span: 0, bins: []};

	const span = tMax - tMin;
	const bins = [];
	for(let s = 0; s < binCount; s++){
		const lo = tMin + span * (s / binCount);
		const hi = tMin + span * ((s + 1) / binCount);
		const inBin = proj.filter((pr) => pr.t >= lo && pr.t <= hi);

		let radius = null;
		if(inBin.length >= 8){
			const circle = fitCircle2D(inBin);
			if(circle) radius = circle.r;
		}

		let coverageDeg = 0;
		if(inBin.length > 0){
			const covered = new Array(36).fill(false);
			for(const pr of inBin){
				let angle = Math.atan2(pr.y, pr.x);
				if(angle < 0) angle += Math.PI * 2;
				covered[Math.min(35, Math.floor((angle / (Math.PI * 2)) * 36))] = true;
			}
			coverageDeg = (covered.filter(Boolean).length / 36) * 360;
		}

		bins.push({count: inBin.length, radius, coverageDeg});
	}

	return {span, bins};
}

// Given a finished axis+radius, the highest/lowest points of the circular
// cross-section *in true 3D* (not a naive centerZ +/- radius, which is only
// correct for a perfectly horizontal axis - see module doc / spec §22).
// `center` is any point on the axis (typically the Measurement Station's
// projection onto it).
export function verticalExtentOfCrossSection(center, axisDir, radius){
	const {u, v} = orthonormalBasis(axisDir);
	// Z(theta) = centerZ + radius*(cos(theta)*u.z + sin(theta)*v.z) is a
	// sinusoid in theta; its amplitude is radius*length([u.z, v.z])
	const amplitude = radius * Math.hypot(u.z, v.z);
	return {
		topZ: center.z + amplitude,
		bottomZ: center.z - amplitude,
	};
}

// closest point on the infinite axis line to `point` - used to project the
// direction-points' midpoint onto the finished axis for the Measurement
// Station (spec §20)
export function closestPointOnAxis(point, axisPoint, axisDir){
	const t = paramAlongAxis(point, axisPoint, axisDir);
	return axisPoint.clone().addScaledVector(axisDir, t);
}
